import { Audience, Category, Gender, Interest, Pronoun, Title, Topic } from "./category";
import { City } from "./geography";

export interface CollectionFull {
  id: string;
  created_date: string;
  updated_date: string | null;
  name: string;
  description: string;
  theme_1: Theme | null;
  theme_2: Theme | null;
  theme_3: Theme | null;
  hero_posts: Post[];
  images: string[];
  quote: {
    text: string;
    author: string;
    source: string;
  };
  slug: string;
  /**
   * Members-only gate, from the API.
   *
   * `members_only` is what an admin set; `locked` is the answer for *this*
   * visitor -- true only when they are not signed in. A locked payload has had
   * its content withheld server-side (no `url`, no `data`), so the UI is
   * showing a teaser and must not pretend otherwise.
   */
  members_only?: boolean;
  locked?: boolean;
}


export interface Theme {
  id: string;
  created_date: string;
  updated_date: string | null;
  name: string;
  summary: string;
  posts: Post[];
  hero_posts: Post[];
  slug: string;
}


export interface CollectionMinimised {
  id: string;
  created_date: string;
  updated_date: string | null;
  name: string;
  description: string;
  images: string[];
  quote: {
    text: string;
    author: string;
    source: string;
  };
  slug: string;
  /**
   * Members-only gate, from the API.
   *
   * `members_only` is what an admin set; `locked` is the answer for *this*
   * visitor -- true only when they are not signed in. A locked payload has had
   * its content withheld server-side (no `url`, no `data`), so the UI is
   * showing a teaser and must not pretend otherwise.
   */
  members_only?: boolean;
  locked?: boolean;
}

export interface Author {
  id: string;
  created_date: string;
  updated_date: string | null;
  name: string;
  status: string;
  slug: string;
  featured: boolean;
  feature_start_date: string | null;
  feature_end_date: string | null;
  content_user_agreement_in_place: boolean;
}

export interface Tag {
  id: string;
  name: string;
  image: string | null;
  tags: Tag[];
}

export interface Post {
  id: string;
  created_date: string;
  updated_date: string | null;
  author_published_date: string | null;
  /** Null when the item is locked -- the API withholds it. */
  url: string | null;
  consumption: Category[];
  title: string;
  display_title: string;
  authors: Author[];
  guests: Author[];
  data: string;
  synopsis: string;
  topics: Topic[];
  primary_tags: Tag[];
  display_primary_tags: Tag[];
  secondary_tags: Tag[];
  price: string;
  duration: string;
  neuro_affirming: boolean;
  approved_for_site: boolean;
  pending_review: boolean;
  featured: boolean;
  comfort_level: string;
  order: number;
  status: string;
  slug: string;
  rating: number;
  reviews: number;
  images: string[];
  collections: CollectionMinimised[];
  /**
   * Members-only gate, from the API.
   *
   * `members_only` is what an admin set; `locked` is the answer for *this*
   * visitor -- true only when they are not signed in. A locked payload has had
   * its content withheld server-side (no `url`, no `data`), so the UI is
   * showing a teaser and must not pretend otherwise.
   */
  members_only?: boolean;
  locked?: boolean;
  /**
   * Only present on search/list payloads.
   *
   * The serializers do not include it (see utils/response.py); the search and
   * list views merge it onto each object to disambiguate a mixed Post/Media
   * result set. It is absent from the profile payload, so anything reading a
   * favourites list must not rely on it — declaring it required let
   * `obj.content_type` typecheck there while being `undefined` at runtime.
   */
  content_type?: string;
}

/**
 * The object types that can be favourited.
 *
 * These strings are matched verbatim in three places, so a typo fails silently
 * rather than loudly:
 *   - `FavouriteButton` switches on it to pick which `profile.favourite_*` list to
 *     check, and falls through to "not favourited" on no match;
 *   - the API resolves it to a model in `AddToFavourites.get_model`, returning
 *     400 "Invalid object_type" on no match;
 *   - `profile.favourite_<type>s` is string-built server-side from it.
 *
 * Declaring the prop as a plain `string` let `"Entry"` (should be
 * `"DirectoryEntry"`) and `undefined` reach the button and render an empty heart
 * on content the user had actually favourited. Keep this union in sync with the
 * branches in FavouriteButton and with the server's get_model.
 *
 * Note this is *not* the same set as the rateable types: `Support` can be rated
 * but not favourited (Profile has no `favourite_supports` field).
 */
export type FavouriteObjectType =
  | "Post"
  | "People"
  | "Event"
  | "Tool"
  | "Media"
  | "Topic"
  | "Collection"
  | "DirectoryEntry";

/**
 * Types a card can represent. Wider than {@link FavouriteObjectType} because a card
 * also uses this value for view logging and ratings, and `Support` participates in
 * those but cannot be favourited.
 */
export type CardObjectType = FavouriteObjectType | "Support";

/** Narrows a card's type to one the favourite button can actually handle. */
export function isFavouritable(
  objectType: CardObjectType
): objectType is FavouriteObjectType {
  // `CardObjectType` is `FavouriteObjectType | "Support"`, so ruling out the one
  // extra member is the whole check — a parallel list of the other eight could only
  // ever drift out of sync with the union.
  return objectType !== "Support";
}

export interface Event {
  id: string;
  created_date: string;
  updated_date: string | null;
  host: string;
  title: string;
  synopsis: string;
  start_date: string | null;
  end_date: string | null;
  city: City;
  city_details: string;
  topics: Topic[];
  tags: Tag[];
  audience: Audience | null;
  slug: string;
  rating: number;
  reviews: number;
  images: string[];
  price: string;
  collections: CollectionMinimised[];
  online: boolean;
  url: string;
  /** False once the event has started. The API returns past events too. */
  is_upcoming: boolean;
}

export interface People {
  id: string;
  created_date: string;
  updated_date: string | null;
  name: string;
  profession: string;
  synopsis: string;
  tags: Tag[];
  topics: Topic[];
  url: string;
  slug: string;
  rating: number;
  reviews: number;
  images: string[];
  collections: CollectionMinimised[];
  status: string;
}

export interface Media {
  id: string;
  created_date: string;
  updated_date: string | null;
  author_published_date: string | null;
  authors: Author[];
  guests: Author[];
  data: string;
  title: string;
  display_title: string;
  description: string;
  synopsis: string;
  primary_tags: Tag[];
  display_primary_tags: Tag[];
  secondary_tags: Tag[];
  topics: Topic[];
  interests: Interest[];
  audience: Audience | null;
  category: Category | null;
  consumption: Category[];
  url: string;
  price: string;
  duration: string;
  transcript: string;
  slug: string;
  neuro_affirming: boolean;
  approved_for_site: boolean;
  pending_review: boolean;
  featured: boolean;
  comfort_level: string;
  rating: number;
  reviews: number;
  images: string[];
  collections: CollectionMinimised[];
  /**
   * Members-only gate, from the API. See the note on `Post`.
   */
  members_only?: boolean;
  locked?: boolean;
  /**
   * Only present on search/list payloads.
   *
   * The serializers do not include it (see utils/response.py); the search and
   * list views merge it onto each object to disambiguate a mixed Post/Media
   * result set. It is absent from the profile payload, so anything reading a
   * favourites list must not rely on it — declaring it required let
   * `obj.content_type` typecheck there while being `undefined` at runtime.
   */
  content_type?: string;
}

export interface Tool {
  id: string;
  created_date: string;
  updated_date: string | null;
  name: string;
  synopsis: string;
  tags: Tag[];
  topics: Topic[];
  price: string;
  slug: string;
  url: string;
  rating: number;
  reviews: number;
  images: string[];
  collections: CollectionMinimised[];
  status: string;
}

export interface Support {
  id: string;
  created_date: string;
  updated_date: string | null;
  name: string;
  synopsis: string;
  city: City;
  tags: Tag[];
  topics: Topic[];
  price: number;
  slug: string;
  url: string;
  rating: number;
  reviews: number;
  images: string[];
  collections: CollectionMinimised[];
  status: string;
}